import React from "react";
import { BRAND, MINT, NIGHT_2, BRAND_DEEP, NIGHT, TEXT_INK, TEXT_DARK, TEXT_DIM_ON_DARK } from "../tokens";

interface PillProps {
  icon?: React.ComponentType<{ size?: number }>;
  children: React.ReactNode;
  dark?: boolean;
}

export function Pill({ icon: Icon, children, dark }: PillProps) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "5px 12px", borderRadius: 999,
      background: dark ? "rgba(47, 227, 190, 0.10)" : "rgba(15, 149, 153, 0.08)",
      border: "0.5px solid " + (dark ? "rgba(47, 227, 190, 0.30)" : "rgba(15, 149, 153, 0.20)"),
      color: dark ? MINT : BRAND, fontSize: 12, fontWeight: 600, letterSpacing: "0.01em",
    }}>
      {Icon && <Icon size={13} />} {children}
    </span>
  );
}

interface PhotoFrameProps {
  aspectRatio?: string;
  label: string;
  hint?: string;
  accent?: string;
}

export function PhotoFrame({ aspectRatio, label, hint, accent }: PhotoFrameProps) {
  return (
    <div style={{
      width: "100%", aspectRatio: aspectRatio || "1/1", borderRadius: 18, overflow: "hidden",
      position: "relative",
      background: "linear-gradient(135deg, " + (accent || NIGHT_2) + " 0%, " + BRAND_DEEP + " 60%, " + NIGHT + " 100%)",
      display: "grid", placeItems: "center",
      color: "rgba(255,255,255,0.55)", fontSize: 11, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase",
    }}>
      <div style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 30% 30%, rgba(47, 227, 190, 0.18), transparent 60%)" }} />
      <div style={{ position: "relative", textAlign: "center", padding: 16 }}>
        <div>{label}</div>
        {hint && <div style={{ fontSize: 10, fontWeight: 500, color: "rgba(255,255,255,0.4)", marginTop: 6, textTransform: "none", letterSpacing: 0 }}>{hint}</div>}
      </div>
    </div>
  );
}

interface HeroStatProps { top: string; bottom: string; }
export function HeroStat({ top, bottom }: HeroStatProps) {
  return (
    <div>
      <div style={{ fontSize: 18, fontWeight: 800, color: MINT, letterSpacing: "-0.01em" }}>{top}</div>
      <div style={{ fontSize: 12.5, color: TEXT_DIM_ON_DARK, marginTop: 3 }}>{bottom}</div>
    </div>
  );
}

interface RoundButtonProps {
  icon: React.ComponentType<{ size?: number; color?: string }>;
  label: string;
  color: string;
}
export function RoundButton({ icon: Icon, label, color }: RoundButtonProps) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
      <div style={{ width: 52, height: 52, borderRadius: "50%", background: color, display: "grid", placeItems: "center", boxShadow: "0 4px 10px " + color + "33" }}>
        <Icon size={22} color="#FFFFFF" />
      </div>
      <span style={{ fontSize: 10, fontWeight: 600, color: TEXT_INK }}>{label}</span>
    </div>
  );
}

interface LegendProps { color: string; label: string; value: string; }
export function Legend({ color, label, value }: LegendProps) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
        <span style={{ width: 8, height: 8, borderRadius: 2, background: color }} />
        <span style={{ fontSize: 10, color: TEXT_DARK }}>{label}</span>
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, color: TEXT_INK, fontVariantNumeric: "tabular-nums" }}>{value}</span>
    </div>
  );
}